import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiRequest } from '../api/apiClient';

const UserDataContext = createContext();

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

export const UserDataProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [customLists, setCustomLists] = useState([]);
  const [visitHistory, setVisitHistory] = useState([]);
  const [userPhotos, setUserPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const toLocalDate = (d) => {
    try {
      return new Date(d).toISOString().slice(0, 10);
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  };

  const isoFromLocalDate = (localDate) => {
    // backend LocalDate JSON is typically "YYYY-MM-DD"
    if (!localDate) return new Date().toISOString();
    return new Date(`${localDate}T00:00:00.000Z`).toISOString();
  };

  const mapVisitDtoToHistoryEntry = (dto) => ({
    fortId: dto.fortId,
    timestamp: isoFromLocalDate(dto.visitedOn),
    notes: dto.notes
  });

  const mapReviewDtoToFrontendReview = (dto) => {
    const userDisplayName = dto.userDisplayName ?? dto.user_display_name ?? '';
    const userId = dto.userId ?? dto.user_id ?? null;
    const createdAt = dto.createdAt ?? dto.created_at ?? null;
    const visitedOn = dto.visitedOn ?? dto.visited_on ?? null;
    const dateIso = visitedOn
      ? isoFromLocalDate(visitedOn)
      : createdAt
        ? new Date(createdAt).toISOString()
        : new Date().toISOString();
    const isSelf = user && userId === user.id;
    const displayName = userDisplayName || (isSelf ? user.name : 'Visitor');

    return {
      id: dto.id,
      fortId: dto.fortId ?? dto.fort_id,
      userId,
      rating: dto.rating,
      title: dto.title,
      comment: dto.comment,
      date: dateIso,
      userName: displayName,
      userAvatar: isSelf
        ? user.avatar
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random&color=fff`,
    };
  };

  // Load user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserData();
    } else {
      // Clear data when logged out
      setFavorites([]);
      setReviews([]);
      setCustomLists([]);
      setVisitHistory([]);
      setUserPhotos([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  const loadUserData = async () => {
    setIsLoading(true);
    try {
      const storedLists = localStorage.getItem(`customLists_${user.id}`);
      const storedHistory = localStorage.getItem(`visitHistory_${user.id}`);
      const storedPhotos = localStorage.getItem(`userPhotos_${user.id}`);

      // Still use localStorage for non-core UX features.
      setCustomLists(storedLists ? JSON.parse(storedLists) : []);
      setUserPhotos(storedPhotos ? JSON.parse(storedPhotos) : []);

      // Favorites (API)
      const favJson = await apiRequest('/api/v1/users/me/favorites', {
        query: { page: 0, size: 1000 }
      });
      const favFortIds = (favJson?.content || []).map((f) => f.fortId);
      setFavorites(favFortIds);

      // Visits (API)
      const visitsJson = await apiRequest('/api/v1/users/me/visits', {
        query: { page: 0, size: 1000 }
      });
      const mappedVisits = (visitsJson?.content || []).map(mapVisitDtoToHistoryEntry);
      setVisitHistory(mappedVisits);

      // Reviews: load for a small subset (favorites + recent visits).
      const reviewFortIds = Array.from(new Set([
        ...favFortIds,
        ...mappedVisits.map((v) => v.fortId)
      ])).slice(0, 10);

      const loadedReviews = [];
      for (const fortId of reviewFortIds) {
        const rJson = await apiRequest(`/api/v1/forts/${fortId}/reviews`, {
          query: { page: 0, size: 50 }
        });
        const content = rJson?.content || [];
        loadedReviews.push(...content.map(mapReviewDtoToFrontendReview));
      }
      setReviews(loadedReviews);
    } catch (error) {
      console.error('Error loading user data:', error);
      // Fallback to "local" lists/photos if backend is temporarily unavailable.
      const storedLists = localStorage.getItem(`customLists_${user.id}`);
      const storedPhotos = localStorage.getItem(`userPhotos_${user.id}`);
      setCustomLists(storedLists ? JSON.parse(storedLists) : []);
      setUserPhotos(storedPhotos ? JSON.parse(storedPhotos) : []);
      setFavorites([]);
      setReviews([]);
      setVisitHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveUserData = () => {
    if (!isAuthenticated || !user) return;
    localStorage.setItem(`customLists_${user.id}`, JSON.stringify(customLists));
    localStorage.setItem(`userPhotos_${user.id}`, JSON.stringify(userPhotos));
  };

  // Save to localStorage whenever data changes
  useEffect(() => {
    saveUserData();
  }, [customLists, userPhotos]);

  const addToFavorites = async (fortId) => {
    const normalizedFortId = Number(fortId);
    if (!user || favorites.includes(normalizedFortId)) return;

    // backend will create favorite (idempotent via FK + unique constraint)
    await apiRequest('/api/v1/users/me/favorites', {
      method: 'POST',
      query: { fortId: normalizedFortId }
    });
    setFavorites(prev => (prev.includes(normalizedFortId) ? prev : [...prev, normalizedFortId]));
  };

  const removeFromFavorites = async (fortId) => {
    const normalizedFortId = Number(fortId);
    if (!user) return;

    await apiRequest(`/api/v1/users/me/favorites/${normalizedFortId}`, {
      method: 'DELETE'
    });
    setFavorites(prev => prev.filter((id) => id !== normalizedFortId));
  };

  const isFavorite = (fortId) => favorites.includes(fortId);

  const addReview = async (fortId, reviewData) => {
    if (!user) return;
    const normalizedFortId = Number(fortId);

    const visitedOn = toLocalDate(new Date());
    const json = await apiRequest(`/api/v1/forts/${normalizedFortId}/reviews`, {
      method: 'POST',
      body: {
        rating: reviewData.rating,
        title: reviewData.title,
        comment: reviewData.comment,
        visitedOn
      }
    });

    const mapped = mapReviewDtoToFrontendReview(json);
    setReviews((prev) => prev.filter((r) => !(r.fortId === normalizedFortId && r.userId === user.id)).concat([mapped]));
  };

  const updateReview = async (reviewId, updates) => {
    const existing = reviews.find((r) => r.id === reviewId);
    if (!existing || !user) return;

    const fortId = existing.fortId;
    const visitedOn = toLocalDate(existing.date || new Date());

    const json = await apiRequest(`/api/v1/forts/${fortId}/reviews`, {
      method: 'POST',
      body: {
        rating: updates.rating ?? existing.rating,
        title: updates.title ?? existing.title,
        comment: updates.comment ?? existing.comment,
        visitedOn
      }
    });

    const mapped = mapReviewDtoToFrontendReview(json);
    setReviews((prev) => prev.filter((r) => !(r.fortId === fortId && r.userId === user.id)).concat([mapped]));
  };

  const deleteReview = async (reviewId) => {
    const existing = reviews.find((r) => r.id === reviewId);
    if (!existing || !user) return;

    await apiRequest(`/api/v1/forts/${existing.fortId}/reviews/${reviewId}`, {
      method: 'DELETE'
    });

    setReviews((prev) => prev.filter((review) => review.id !== reviewId));
  };

  const getReviewsForFort = (fortId) => {
    return reviews.filter(review => review.fortId === fortId);
  };

  const getUserReviewForFort = (fortId) => {
    return reviews.find(review => review.fortId === fortId && review.userId === user.id);
  };

  const createCustomList = (listData) => {
    const newList = {
      id: Date.now(),
      userId: user.id,
      ...listData,
      forts: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setCustomLists(prev => [...prev, newList]);
    return newList;
  };

  const updateCustomList = (listId, updates) => {
    setCustomLists(prev => prev.map(list => 
      list.id === listId ? { ...list, ...updates, updatedAt: new Date().toISOString() } : list
    ));
  };

  const deleteCustomList = (listId) => {
    setCustomLists(prev => prev.filter(list => list.id !== listId));
  };

  const addFortToList = (listId, fortId) => {
    setCustomLists(prev => prev.map(list => 
      list.id === listId 
        ? { ...list, forts: [...new Set([...list.forts, fortId])] }
        : list
    ));
  };

  const removeFortFromList = (listId, fortId) => {
    setCustomLists(prev => prev.map(list => 
      list.id === listId 
        ? { ...list, forts: list.forts.filter(id => id !== fortId) }
        : list
    ));
  };

  const addToVisitHistory = (fortId) => {
    if (!user) return;
    const normalizedFortId = Number(fortId);
    const visitedOn = toLocalDate(new Date());

    apiRequest('/api/v1/users/me/visits', {
      method: 'POST',
      body: {
        fortId: normalizedFortId,
        visitedOn,
        notes: ''
      }
    })
      .then((json) => {
        const mapped = mapVisitDtoToHistoryEntry(json);
        setVisitHistory((prev) => {
          const filtered = prev.filter((v) => v.fortId !== normalizedFortId);
          return [mapped, ...filtered].slice(0, 50);
        });
      })
      .catch((e) => {
        console.error('Failed to add visit history:', e);
      });
  };

  const getVisitStats = () => {
    const uniqueForts = new Set(visitHistory.map(v => v.fortId));
    return {
      totalVisits: visitHistory.length,
      uniqueFortsVisited: uniqueForts.size,
      mostVisited: getMostVisitedForts()
    };
  };

  const getMostVisitedForts = () => {
    const counts = {};
    visitHistory.forEach(visit => {
      counts[visit.fortId] = (counts[visit.fortId] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([fortId, count]) => ({ fortId, count }));
  };

  const uploadPhoto = (fortId, photoData) => {
    const newPhoto = {
      id: Date.now(),
      fortId,
      userId: user.id,
      userName: user.name,
      userAvatar: user.avatar,
      ...photoData,
      uploadedAt: new Date().toISOString(),
      status: 'pending' // pending, approved, rejected
    };
    setUserPhotos(prev => [...prev, newPhoto]);
    return newPhoto;
  };

  const getUserPhotosForFort = (fortId) => {
    return userPhotos.filter(photo => photo.fortId === fortId && photo.status === 'approved');
  };

  const value = {
    // State
    favorites,
    reviews,
    customLists,
    visitHistory,
    userPhotos,
    isLoading,
    
    // Favorites
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    
    // Reviews
    addReview,
    updateReview,
    deleteReview,
    getReviewsForFort,
    getUserReviewForFort,
    
    // Custom Lists
    createCustomList,
    updateCustomList,
    deleteCustomList,
    addFortToList,
    removeFortFromList,
    
    // Visit History
    addToVisitHistory,
    getVisitStats,
    
    // Photos
    uploadPhoto,
    getUserPhotosForFort
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
